import classNames from 'classnames';
import 'dayjs/locale/en';
import 'dayjs/locale/ru';
import { observer } from 'mobx-react-lite';
import { FC, useMemo, useRef } from 'react';

import { LoadingBar } from '@gem/ui-kit';

import { makeCalendarEvent } from './CalendarEvent/makeCalendarEvent';
import { useContentPresenter } from './Content.presenter';
import styles from './Content.styles.m.css';
import { DnDCalendar } from './DnDCalendar/DnDCalendar';
import { makeDayHeader } from './Header/DayHeader/DayHeader';
import { RightSidebar } from './RightSidebar/RightSidebar';
import { makeToolbar } from './Toolbar/MakeToolbar';

interface IContent {
  isCompact?: boolean;
}

export const Content: FC<IContent> = observer(({ isCompact }) => {
  const calRef = useRef<HTMLDivElement>(null);
  const {
    presenter,
    presenterActiveSidebarView,
    presenterDragAndDrop,
    presenterScrollToFirstEvent,
  } = useContentPresenter({ isCompact });

  const Toolbar = useMemo(
    () =>
      makeToolbar({
        onCreate: presenter.onCreate,
        accounts: presenter.accounts,
        isCompact,
      }),
    [presenter.accounts]
  );

  const DayHeader = useMemo(() => makeDayHeader({ isCompact }), [isCompact]);

  const Event = useMemo(
    () =>
      makeCalendarEvent({
        currentView: presenter.currentView,
        getMyEventStatus: presenter.getMyEventStatus,
      }),
    [presenter.currentView]
  );

  return (
    <>
      <div
        className={classNames(`view-${presenter.currentView}`, 'container', {
          'is-compact-view': isCompact,
        })}
        ref={calRef}
      >
        <LoadingBar
          className={styles.loadingBar}
          loading={presenter.isLoading}
        />
        <DnDCalendar
          key={presenterScrollToFirstEvent.calendarKey}
          scrollToTime={presenterScrollToFirstEvent.scrollAnchor}
          events={presenter.gridEvents}
          currentView={presenter.currentView}
          currentDate={presenter.currentDate}
          isWeekView={presenter.isWeekView}
          isDayView={presenter.isDayView}
          changeView={presenter.changeView}
          onNavigate={presenter.onNavigate}
          onSelectEvent={presenter.onSelectEvent}
          onSelectSlot={presenter.onSelectSlot}
          onRangeChange={presenter.onRangeChange}
          onDrillDown={presenter.onDrillDown}
          onDraggableAccessor={presenterDragAndDrop.onDraggableAccessor}
          onDragStart={presenterDragAndDrop.onDragStart}
          onEventDrop={presenterDragAndDrop.onEventDrop}
          Event={Event}
          Toolbar={Toolbar}
          DayHeader={DayHeader}
          calRef={calRef}
        />
      </div>
      {presenterActiveSidebarView.activeSidebarView && (
        <RightSidebar
          activeSidebarView={presenterActiveSidebarView.activeSidebarView}
          openInfoViewSidebar={presenter.onInfoViewSidebar}
          openEditViewSidebar={presenterActiveSidebarView.openEditViewSidebar}
          accounts={presenter.accounts}
          templateEvent={presenter.templateEvent}
          selectedEvent={presenter.selectedEvent}
          isInitiator={presenter.isInitiator}
          updateTemplateEvent={presenter.updateTemplateEvent}
          updateSelectedEvent={presenter.updateSelectedEvent}
          onCloseViewSidebar={presenter.onCloseViewSidebar}
          updateEventAfterUpdateParticipantStatusSuccess={
            presenter.updateEventAfterUpdateParticipantStatusSuccess
          }
          onEditEvent={presenter.onEditEvent}
          addEventsAfterCreateRecurrenceSuccess={presenter.addEventsAfterCreateRecurrenceSuccess}
          addEventAfterCreateSuccess={presenter.addEventAfterCreateSuccess}
          deleteEventsAfterDeleteRecurrenceSuccess={
            presenter.deleteEventsAfterDeleteRecurrenceSuccess
          }
          deleteEventAfterDeleteSuccess={presenter.deleteEventAfterDeleteSuccess}
          jumpTo={presenter.jumpTo}
          myEventStatusBySelectedEvent={presenter.myEventStatusBySelectedEvent}
          minimizeSideBar={presenter.minimizeSideBar}
          isSideBarMinimized={presenter.isSideBarMinimized}
          isCompact={isCompact}
        />
      )}
    </>
  );
});
